import { ReactNode, useMemo } from 'react';

import {
  usePanelBehavior,
  portaledMenuExcludeSelectors,
} from '~/shared/utils/usePanelBehavior';

interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id?: string;
  width?: string;
  top?: number | string;
  zIndexClass?: string;
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  enableClickOutside?: boolean;
  excludeSelectors?: string[];
  onClose?: () => void; // callback to execute when panel closes
}

export default function SidePanel({
  open,
  onOpenChange,
  id,
  width = 'w-[445px]',
  top = 0,
  zIndexClass = 'z-[20]',
  ariaLabel,
  children,
  className = '',
  enableClickOutside = true,
  excludeSelectors = [],
  onClose,
}: SidePanelProps) {
  const mergedExcludeSelectors = useMemo(
    () => [...portaledMenuExcludeSelectors, ...excludeSelectors],
    [excludeSelectors]
  );
  const { panelRef } = usePanelBehavior({
    open,
    onOpenChange,
    enableClickOutside,
    excludeSelectors: mergedExcludeSelectors,
    onClose,
  });

  return (
    <div
      ref={panelRef}
      id={id}
      role={ariaLabel ? 'region' : undefined}
      aria-label={ariaLabel}
      className={`fixed right-0 ${zIndexClass} ${width} bg-white border-l border-primary-70 transform transition-transform duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      } ${className}`}
      style={{
        top,
        bottom: 0,
        boxShadow: '-15px 86px 57.4px -3px rgba(0, 0, 0, 0.09)',
      }}
    >
      {children}
    </div>
  );
}
